import { useMemo, useState } from 'react';
import { CalendarClockIcon } from '@hugeicons/core-free-icons';
import { format, isValid, parseISO } from 'date-fns';
import { cn } from '../../lib/cn';
import { Button } from './Button';
import { Calendar } from './calendar';
import { Field, FieldLabel } from './field';
import { HugeIcon } from './HugeIcon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export function DatePicker({
  className,
  label,
  name,
  onChange,
  required = false,
  value,
}: {
  className?: string;
  label: string;
  name?: string;
  onChange: (value: string) => void;
  required?: boolean;
  value: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => {
    if (!value) return undefined;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : undefined;
  }, [value]);

  return (
    <Field className={cn('w-full min-w-0 gap-1.5', className)}>
      <FieldLabel className="text-xs text-muted-foreground">{label}</FieldLabel>
      <input name={name} required={required} type="hidden" value={value} />
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger render={<Button className="w-full justify-between rounded-xl border-border/80 bg-card px-3 font-normal shadow-[0_1px_2px_rgba(15,23,42,0.04)]" type="button" variant="outline" />}>
          <span className={cn(!selected && 'text-muted-foreground')}>{selected ? format(selected, 'MMM d, yyyy') : 'Choose a date'}</span>
          <HugeIcon className="text-muted-foreground" icon={CalendarClockIcon} size={16} />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto overflow-hidden rounded-2xl border border-border/80 bg-popover p-0 shadow-xl">
          <Calendar
            className="rounded-2xl bg-popover"
            defaultMonth={selected}
            mode="single"
            onSelect={(date) => {
              if (!date) return;
              onChange(format(date, 'yyyy-MM-dd'));
              setOpen(false);
            }}
            selected={selected}
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
}
